import clsx from "clsx";
import React, { useEffect, useState } from "react";
import BranchLink from "web/react/components/branch-link/branch-link";
import { RawBranchLink } from "web/script/utils/branch-io";
import styles from "./image-carousel.module.css";

interface ImageCarouselProps {
    branchLink?: RawBranchLink;
    carouselItems: JSX.Element[];
    showDots: boolean;
    scrollTime?: number;
    transition?: "slide" | "fade";
}

function ImageCarousel({
    carouselItems,
    showDots,
    branchLink,
    scrollTime = 3000,
    transition = "slide",
}: ImageCarouselProps): JSX.Element {
    const [carouselCounter, setCarouselCounter] = useState<number>(0);
    const slideTrans = transition === "slide";

    useEffect(() => {
        let counter = carouselCounter;

        const carousel = setInterval(() => {
            setCarouselCounter(counter);
            if (counter === carouselItems.length - 1) {
                counter = 0;
            } else {
                counter += 1;
            }
        }, scrollTime);

        return (): void => {
            clearInterval(carousel);
        };
    }, [carouselItems, scrollTime, carouselCounter]);

    function carouselIndicatorDot(index): JSX.Element {
        const isSelected = index === carouselCounter;
        return (
            <li className={styles.carouselNavigationItem} key={index}>
                <div
                    className={clsx(styles.carouselNavigationDot, {
                        [styles.carouselNavigationDotSelected]: isSelected,
                    })}
                ></div>
            </li>
        );
    }

    function carouselItem(innerElement, index): JSX.Element {
        return (
            <li
                className={slideTrans ? styles.carouselSlide : styles.carouselFade}
                key={`slider-${index}`}
            >
                {slideTrans ? (
                    <div
                        style={{ transform: `translateX(-${carouselCounter}00%)` }}
                        className={styles.carouselSnapper}
                    >
                        {innerElement}
                    </div>
                ) : (
                    <div
                        className={clsx(styles.carouselFader, {
                            [styles.nonSelect]: carouselCounter !== index,
                        })}
                    >
                        {innerElement}
                    </div>
                )}
            </li>
        );
    }
    const sliderItems: JSX.Element[] = [];
    const slideIndicatorDots: JSX.Element[] = [];
    for (let i = 0; i < carouselItems.length; ++i) {
        sliderItems.push(carouselItem(carouselItems[i], i));
        if (showDots) {
            slideIndicatorDots.push(carouselIndicatorDot(i));
        }
    }

    let touchStart = 0;
    function handleTouchStart(e): void {
        touchStart = e.touches[0].clientX;
    }
    function handleTouchEnd(e): void {
        if (e.changedTouches[0].clientX > touchStart) {
            // swipe right moving negatively down the array
            let count = carouselCounter - 1;
            if (count < 0) {
                count = carouselItems.length - 1;
            }
            setCarouselCounter(count);
        } else if (e.changedTouches[0].clientX < touchStart) {
            // swipe left moving positively up the array
            let count = carouselCounter + 1;
            if (count > carouselItems.length - 1) {
                count = 0;
            }
            setCarouselCounter(count);
        }
    }

    return (
        <div className={styles.carousel}>
            {branchLink ? (
                <BranchLink
                    branchLink={branchLink}
                    eventLabel={"in_feed_app_benefits"}
                    title={"in_feed_app_benefits"}
                >
                    <>
                        <ol className={styles.carouselList}>{sliderItems}</ol>
                        <div className={styles.carouselNavigation}>
                            <ol className={styles.carouselNavigationList}>{slideIndicatorDots}</ol>
                        </div>
                    </>
                </BranchLink>
            ) : (
                <>
                    <ol
                        className={styles.carouselList}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        {sliderItems}
                    </ol>
                    <div className={styles.carouselNavigation}>
                        <ol className={styles.carouselNavigationList}>{slideIndicatorDots}</ol>
                    </div>
                </>
            )}
        </div>
    );
}
export default ImageCarousel;
